import React, { FC, useEffect } from 'react'
import t from 'src/translations/main'
import utils, { checkMode } from 'src/components/Organisms/Form/utils'
import useFetch from 'src/components/Core/Hooks/useFetch/useFetch'
import updateModal from 'src/components/Sections/Modal/updateModal'
import {
  useCountryOfResidences,
  useCountryOfResidencesUpdate,
  useDropdownOptions,
} from 'src/components/Core/Context/OnboardingContext'

import Button from 'src/components/Atoms/Button/Button'
import Table from 'src/components/Organisms/Table/Table'
import Font from 'src/components/Core/Typography/Font/Font'
import ModalCountryOfResidency from './ModalCountryOfResidency/ModalCountryOfResidency'

import {
  ApiData,
  FormProps,
  TableConfigParam,
  TableObj,
} from '../../../../../Pages/Onboarding/types'
import styled from 'styled-components'
import { CustomValueType } from 'src/components/Organisms/Form/types'
import { getEndpointUrl } from 'src/components/Core/Utils/url_constants'
import storage from 'src/components/Core/Utils/storage'

const TableSectionWrapper = styled.div`
margin-top: 16px;
`

const tableConfig: TableConfigParam[] = [
  { key: 'country' },
  { key: 'tin' },
  { key: 'reason' },
  { key: 'reasonForNotObtainTIN' },
  {
    name: 'pencil',
    color: 'ultraViolet',
  },
  {
    name: 'close',
    color: 'error',
  },
  { key: 'isTINAvailable' }
]

const tableColumns = [
  {
    Header: t(
      'onboarding.forms.CorporateAccount.TaxResidencyForm.table.col1'
    ),
    accessor: 'col1',
  },
  {
    Header: t(
      'onboarding.forms.CorporateAccount.TaxResidencyForm.table.col2'
    ),
    accessor: 'col2',
  },
  {
    Header: t(
      'onboarding.forms.CorporateAccount.TaxResidencyForm.table.col3'
    ),
    accessor: 'col3',
  },
  {
    Header: t(
      'onboarding.forms.CorporateAccount.TaxResidencyForm.table.col4'
    ),
    accessor: 'col4',
  },
  {
    Header: '',
    accessor: 'col5',
  },
  {
    Header: '',
    accessor: 'col6',
  },
]

const initialRenderFlag = true

const CountryOfResidency: FC<FormProps & {customOnChange?: (args?: ApiData) => void, value?: CustomValueType;}> = props => {
  const tableData = useCountryOfResidences()
  const updateTableData = useCountryOfResidencesUpdate()
  const { dropdownOptions } = useDropdownOptions()
  const { value } = props
  const { sendRequest } = useFetch()
  const applicationId = storage?.get(storage?.keys?.onboarding?.applicationId)

  const editCountryOfResidence = (id: number) => {
    updateModal({
      title: t('onboarding.modals.CountryOfResidency.title'),
      modalState: 'open',
      content: (
        <ModalCountryOfResidency
          actionType='edit'
          action={action}
          tableConfig={tableConfig}
          postUrl={getEndpointUrl('DeliverableAOF/updatecountryofresidencydetails07')}
          id={id}
        />
      ),
      removeFooter: true,
    })
  }

  const deleteCountryOfResidence = (id: number) => {
    const modifyResponse = (data: ApiData) => {
      if (data && typeof data === 'string' && (data as string).toLowerCase() === 'true') {
        updateTableData((prevState: TableObj[]) => {
          return utils.deleteTableRow([...prevState], id)
        })
        updateModal({ modalState: 'close' })
      }
    }
    sendRequest(
      {
        scope: 'onboarding',
        url: getEndpointUrl('DeliverableAOF/deletecountryofresidencydetails07', applicationId, 'deleteCountryOfResidence.json', `CountryOfResidenceId=${id}`),
        method: 'DELETE',
      },
      modifyResponse
    )
  }

  const openConfirmDeleteModal = (id: number) => {
    updateModal({
      title: t('onboarding.modals.CountryOfResidency.delete.title'),
      modalState: 'open',
      content: (
        <Font>
          {t('onboarding.modals.CountryOfResidency.delete.content')}
        </Font>
      ),
      footerActions: [
        {
          label: t('components.forms.labels.yes'),
          version: 2,
          onClick: () => {
            deleteCountryOfResidence(id)
          },
        },
        {
          label: t('components.forms.labels.no'),
          onClick: () => {
            updateModal({ modalState: 'close' })
          },
        },
      ],
    })
  }

  const action = (id: number, action: string) => {
    if (action === 'edit') editCountryOfResidence(id)
    if (action === 'delete') openConfirmDeleteModal(id)
  }

  const createTableData = (
    data: ApiData,
    tableConfig: TableConfigParam[]
  ) => {
    const newTableData: TableObj[] = []
    data?.forEach((rowDataObj: ApiData) => {
      const rowDataObjMapped = {
        ...rowDataObj,
        country: dropdownOptions?.countries?.find(country => country?.index?.toString() === rowDataObj?.country)?.label,
        reason: rowDataObj?.reason !== '' ? `Reason ${rowDataObj?.reason === '0' ? 'A' : rowDataObj.reason === '1' ? 'B' : 'C'}` : ''
      }
      const tableObj = utils.createTableObj(
        rowDataObjMapped,
        tableConfig,
        action
      )
      newTableData.push(tableObj)
    })

    return newTableData
  }

  useEffect(() => {
    if (initialRenderFlag !== true) return

    if (Array.isArray(value) && value?.length !== 0) {
      updateTableData(
        createTableData(value, tableConfig)
      )

    }
  }, [])

  return (
    <TableSectionWrapper>

      <Button
        version={2}
        label={t('onboarding.modals.CountryOfResidency.openButton')}
        onClick={() =>
          updateModal({
            title: t('onboarding.modals.CountryOfResidency.title'),
            modalState: 'open',
            content: (
              <ModalCountryOfResidency
                actionType='add'
                action={action}
                tableConfig={tableConfig}
                postUrl={getEndpointUrl('DeliverableAOF/addcountryofresidencydetails07')}
              />
            ),
            removeFooter: true,
          })
        }
        disabled={checkMode('readOnly')}
      />
      {tableData?.length !== 0 && (
        <Table
          tableData={tableData}
          tableColumns={tableColumns}
          tableReadOnly={checkMode('readOnly')}
        />
      )}
    </TableSectionWrapper>
  )
}

export default CountryOfResidency
